import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drop, PencilSimple, ArrowClockwise, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PRODUCT_TONE = {
  "Presol":        { bg: "from-sky-500 to-sky-700",       chip: "bg-sky-50 text-sky-800 border-sky-200" },
  "Setengah Jadi": { bg: "from-indigo-500 to-indigo-700", chip: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  "Minyak Bakar":  { bg: "from-amber-500 to-amber-700",   chip: "bg-amber-50 text-amber-800 border-amber-200" },
  "Campuran":      { bg: "from-slate-500 to-slate-700",   chip: "bg-slate-50 text-slate-800 border-slate-200" },
  "Soda Api":      { bg: "from-emerald-500 to-emerald-700", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  "Oli Bekas":     { bg: "from-stone-500 to-stone-700",   chip: "bg-stone-50 text-stone-800 border-stone-200" },
};

const toneOf = (p) => PRODUCT_TONE[p] || { bg: "from-slate-500 to-slate-700", chip: "bg-slate-100 text-slate-700 border-slate-200" };

const fmt = (n) => (n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

export default function TankStockPage() {
  const { user } = useAuth();
  const canEdit = ["admin", "owner"].includes(user?.role);
  const canSeeHistory = ["admin", "owner", "admin_kantor"].includes(user?.role);

  const [tanks, setTanks] = useState([]);
  const [options, setOptions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ product: "", current_stock: 0, notes: "" });
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [adjustments, setAdjustments] = useState([]);

  const load = async () => {
    try {
      const [t, o] = await Promise.all([
        api.get("/master/tanks"),
        api.get("/tanks/product-options"),
      ]);
      setTanks(t.data);
      setOptions(o.data.options || []);
    } catch (e) {
      toast.error("Gagal memuat data tangki");
    }
  };
  useAutoRefresh(load);

  const openEdit = (t) => {
    setEditing(t);
    setForm({ product: t.product || "", current_stock: t.current_stock || 0, notes: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        product: form.product || null,
        current_stock: form.current_stock === "" ? null : parseFloat(form.current_stock),
        notes: form.notes || "",
      };
      await api.patch(`/tanks/${editing.id}`, payload);
      toast.success(`Data tangki ${editing.name} diperbarui`);
      setEditing(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get("/tanks/adjustments");
      setAdjustments(data);
    } catch (e) {
      toast.error("Gagal memuat riwayat");
    }
  };

  const totalByProduct = tanks.reduce((acc, t) => {
    const p = t.product || "—";
    acc[p] = (acc[p] || 0) + (t.current_stock || 0);
    return acc;
  }, {});

  return (
    <AppShell
      title="Stok Tangki (Real-time)"
      subtitle={canEdit
        ? "Klik pensil pada tangki untuk overwrite jumlah stok atau ubah jenis minyak."
        : "Ringkasan volume aktual per tangki. Hanya owner/admin yang bisa mengubah."}
      actions={
        <div className="flex items-center gap-2">
          {canSeeHistory && (
            <Button variant="outline" onClick={openHistory} data-testid="tank-history-btn" className="h-10 gap-2">
              <ClockCounterClockwise size={15} weight="bold" /> <span className="hidden sm:inline">Riwayat</span>
            </Button>
          )}
          <Button onClick={load} data-testid="tank-refresh" className="bg-slate-900 hover:bg-slate-800 h-10 gap-2">
            <ArrowClockwise size={15} weight="bold" /> <span className="hidden sm:inline">Muat Ulang</span>
          </Button>
        </div>
      }
    >
      {/* Aggregate strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {options.map((p) => {
          const t = toneOf(p);
          return (
            <div key={p} className={`rounded-lg border p-3 ${t.chip}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{p}</div>
              <div className="font-heading font-black text-xl mt-1 tabular-nums">
                {fmt(totalByProduct[p] || 0)}
                <span className="text-[11px] font-normal opacity-70 ml-1">L</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tanks grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="tank-grid">
        {tanks.map((t) => {
          const tone = toneOf(t.product);
          const pct = t.capacity > 0 ? Math.min(100, ((t.current_stock || 0) / t.capacity) * 100) : 0;
          const critical = pct >= 95;
          return (
            <div key={t.id} className="rounded-xl bg-white border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`tank-card-${t.code}`}>
              <div className={`bg-gradient-to-br ${tone.bg} text-white px-4 py-3 flex items-center justify-between`}>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">{t.code}</div>
                  <div className="font-heading font-bold text-[15px] truncate">{t.name}</div>
                </div>
                <Drop size={26} weight="duotone" className="opacity-90 shrink-0" />
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Jenis Minyak</div>
                  <div className={`inline-block mt-1 px-2 py-0.5 rounded-md border text-[11.5px] font-semibold ${tone.chip}`}>
                    {t.product || "Belum ditentukan"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Stok Aktual</div>
                  <div className="font-heading font-black text-3xl leading-none mt-0.5 tabular-nums text-slate-900">
                    {fmt(t.current_stock)}
                    <span className="text-[12px] font-normal text-slate-400 ml-1.5">L</span>
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-1 font-mono">
                    dari {fmt(t.capacity)} L kapasitas
                  </div>
                </div>
                <div>
                  <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${critical ? "bg-red-500" : "bg-gradient-to-r " + tone.bg}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] font-mono text-slate-500 mt-1">
                    <span>{pct.toFixed(1)}% terisi</span>
                    {critical && <span className="text-red-600 font-bold uppercase">Kritis</span>}
                  </div>
                </div>

                {canEdit && (
                  <button
                    onClick={() => openEdit(t)}
                    data-testid={`tank-edit-${t.code}`}
                    className="w-full mt-1 py-2 rounded-md border border-slate-200 hover:border-sky-500 hover:bg-sky-50 text-slate-700 hover:text-sky-700 text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <PencilSimple size={13} weight="bold" /> Overwrite Stok
                  </button>
                )}
                {t.updated_at && (
                  <div className="text-[10px] font-mono text-slate-400 italic">
                    diubah {new Date(t.updated_at).toLocaleString("id-ID")} · {t.updated_by_name || t.updated_by}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              Overwrite Stok — {editing?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} data-testid="tank-edit-form" className="space-y-4 mt-2">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Jenis Minyak</label>
              <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
                <SelectTrigger data-testid="tank-edit-product" className="h-10 mt-1"><SelectValue placeholder="Pilih jenis minyak" /></SelectTrigger>
                <SelectContent>
                  {options.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Stok Aktual (Liter)</label>
              <Input
                data-testid="tank-edit-stock"
                type="number"
                step="0.01"
                min="0"
                value={form.current_stock}
                onChange={(e) => setForm({ ...form, current_stock: e.target.value })}
                className="h-11 mt-1 font-mono text-lg"
              />
              <div className="text-[10.5px] text-slate-500 font-mono mt-1">
                Kapasitas maksimum: {fmt(editing?.capacity)} L
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Catatan (opsional)</label>
              <Textarea
                data-testid="tank-edit-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Alasan penyesuaian, e.g. hasil opname manual…"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Batal</Button>
              <Button type="submit" data-testid="tank-edit-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white">
                {saving ? "Menyimpan…" : "Simpan Overwrite"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Riwayat Overwrite Tangki</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2" data-testid="tank-history-list">
            {adjustments.length === 0 && (
              <div className="text-center py-10 text-slate-400 text-sm">Belum ada perubahan.</div>
            )}
            {adjustments.map((a) => (
              <div key={a.id} className="p-3 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-heading font-bold text-slate-900 text-[13px]">{a.tank_name}</div>
                  <div className="text-[10px] font-mono text-slate-400">{new Date(a.created_at).toLocaleString("id-ID")}</div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-[12px]">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Sebelum</div>
                    <div className="text-slate-700">{a.before?.product} · <span className="font-mono">{fmt(a.before?.current_stock)} L</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Sesudah</div>
                    <div className="text-emerald-800 font-semibold">{a.after?.product} · <span className="font-mono">{fmt(a.after?.current_stock)} L</span></div>
                  </div>
                </div>
                {a.notes && <div className="text-[11px] text-slate-500 italic mt-1.5">"{a.notes}"</div>}
                <div className="text-[10px] font-mono text-slate-400 mt-1.5">oleh {a.adjusted_by_name || a.adjusted_by}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
